/**
 * API environment switch — dev-only helper.
 *
 * When the dashboard runs on localhost, the developer can point it at the production API
 * instead of the local one to check a UI change against real data. Outside localhost this
 * module is inert and always returns the build-time base URL.
 */
export type ApiEnv = 'local' | 'production';

export const API_ENV_STORAGE_KEY = 'api_env';

const LOCAL_API_BASE_URL = process.env.REACT_APP_API_BASE_URL as string;
const PRODUCTION_API_BASE_URL = process.env.REACT_APP_PRODUCTION_API_BASE_URL || 'https://api.chronos.jobs';

export const isLocalhost = (): boolean =>
    typeof window !== 'undefined' && ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

/** The switch is only offered when running locally against a different API than production. */
export const canSwitchApiEnv = (): boolean => isLocalhost() && LOCAL_API_BASE_URL !== PRODUCTION_API_BASE_URL;

export const getApiEnv = (): ApiEnv => {
    if (!canSwitchApiEnv()) return 'local';
    try {
        return localStorage.getItem(API_ENV_STORAGE_KEY) === 'production' ? 'production' : 'local';
    } catch {
        return 'local';
    }
};

export const getApiBaseUrl = (): string => (getApiEnv() === 'production' ? PRODUCTION_API_BASE_URL : LOCAL_API_BASE_URL);

/**
 * Persist the choice and restart the app on the login screen: auth tokens and the selected
 * agency belong to one backend, so they must not leak into the other.
 */
export const switchApiEnv = (env: ApiEnv): void => {
    if (!canSwitchApiEnv() || env === getApiEnv()) return;
    localStorage.setItem(API_ENV_STORAGE_KEY, env);
    localStorage.removeItem('auth');
    localStorage.removeItem('user_settings');
    window.location.assign('/auth');
};
